<template>
    <BField
        :label="label"
        :type="{ 'is-error': getValidation.invalid }"
        :custom-class="getValidation.invalid ? 'label-error' : ''"
    >
        <BDatepicker
            v-model="localValue"
            v-validate="validation"
            class="ls-datepicker"
            :placeholder="placeholder"
            :min-date="minDate"
            :max-date="maxDate"
            :unselectable-days-of-week="unselectableDaysOfWeek"
            :icon="getValidation.invalid ? 'replaceCalendarError' : 'replaceCalendar'"
            :mobile-native="false"
            :name="name"
            test-id="ls-datepicker"
            :date-formatter="formatDate"
            :disabled="disabled"
            :inline="inline"
        />
    </BField>
</template>

<script>
import moment from 'moment-timezone';
import {
    lsFilters,
    lsPropTypeHelpers,
    lsTrackingService,
    lsValidationService,
} from 'ls-vue-common';
import VueTypes from 'vue-types';
import _ from 'lodash';

export default {
    name: 'LsDatepicker',
    inject: ['$validator'],
    props: {
        value: VueTypes.any.isRequired,
        trackId: VueTypes.custom(lsPropTypeHelpers.trackID).isRequired,
        name: VueTypes.string.isRequired,
        label: VueTypes.string,
        placeholder: VueTypes.string.def('- Select -'),
        validation: VueTypes.object.def(_.constant(null)),
        disabled: VueTypes.bool.def(false),
        inline: VueTypes.bool.def(false),
        minDate: VueTypes.instanceOf(Date).def(() => {
            return moment()
                .startOf('day')
                .toDate();
        }),
        maxDate: VueTypes.instanceOf(Date).def(() => {
            return moment()
                .startOf('day')
                .add(1, 'year')
                .toDate();
        }),
        unselectableDaysOfWeek: VueTypes.array,
    },
    computed: {
        localValue: {
            get() {
                return this.value;
            },
            set(value) {
                lsTrackingService.track({
                    event: 'click',
                    target: this.trackId,
                    data: { value },
                });
                this.$emit('input', value);
            },
        },
        getValidation() {
            return lsValidationService.getValidation.call(this);
        },
    },
    methods: {
        formatDate(date) {
            return lsFilters.date({ date, format: 'monthDateYear' });
        },
    },
};
</script>

<style lang="scss">
.label-error {
    color: $danger !important;
}

.ls-datepicker {
    .input {
        border-color: $secondary-light;
        border-radius: 3px;
        padding-left: 0;
        height: 36px;
    }

    .is-error.input {
        border-color: $danger;
    }

    .control.has-icons-left .input {
        padding-left: 10px;
    }

    .pagination-next {
        order: 3;
    }

    .pagination-next,
    .pagination-previous {
        background-color: $white;
        border: 0;
    }

    // fix weird bug that cropped up with Buefy 0.73
    // likely after this https://github.com/buefy/buefy/issues/1177
    .datepicker-content {
        height: 21.25rem;
    }

    .mdi-replaceCalendar::after {
        content: url(calendar.svg);
        position: absolute;
        top: 5px;
        right: 0;
    }

    .mdi-replaceCalendarError::after {
        content: url(calendarError.svg);
        position: absolute;
        top: 5px;
        right: 0;
    }

    .select select {
        border: 0;
    }

    .control.has-icons-left .icon.is-left {
        left: calc(100% - 35px);
    }

    .mdi-chevron-left::after {
        content: '<';
        color: $secondary;
        font-style: normal;
        font-size: 1.8em;
        font-weight: 100;
    }

    .mdi-chevron-right::after {
        content: '>';
        color: $secondary;
        font-style: normal;
        font-size: 1.8em;
        font-weight: 100;
    }

    // Downward-facing chevrons
    .select:not(.is-multiple):not(.is-loading)::after {
        border: 1.5px solid transparent;
        border-radius: 2px;
        border-right: 0;
        border-top: 0;
        display: block;
        height: 0.625em;
        margin-top: -0.4375em;
        pointer-events: none;
        position: absolute;
        top: 50%;
        transform: rotate(-45deg);
        transform-origin: center;
        width: 0.625em;
    }

    .select:not(.is-multiple):not(.is-loading):not(.is-error)::after {
        border-color: $secondary;
    }

    .dropdown .background {
        background-color: inherit;
    }

    .dropdown.is-mobile-modal > .dropdown-menu {
        width: 100%;

        @media screen and (max-width: 1189px) {
            position: absolute;
            top: 95% !important;
            left: inherit !important;
            transform: none;
            box-shadow: $box-shadow;
        }
    }
}

.label:not(:last-child) {
    font-weight: $regular;
    color: $secondary;
}
</style>
